import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useInView } from "react-intersection-observer";
import confetti from "canvas-confetti";
import {
  Trophy,
  Star,
  TrendingUp,
  ArrowRight,
  Clock,
  Target,
  Rocket,
  Heart,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  GraduationCap,
  Building,
  Calendar,
  X,
  Share2,
  TrendingDown,
  LineChart,
  SearchX,
  Link2,
  Copy,
  CheckCircle2,
  Linkedin,
  Twitter,
  Facebook,
  MessageCircle,
  Mail,
} from "lucide-react";
import HeroSection from "../components/HeroSection";

const ShareButton = ({ story }) => {
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [copied, setCopied] = useState(false);

  // Base URL for your application
  const baseUrl = window.location.origin;
  const shareUrl = `${baseUrl}/success-stories/${story._id}`;
  const shareTitle = `Success Story: ${story.title}`;
  const shareText = `Check out this inspiring success story: ${story.description}`;

  const shareOptions = [
    {
      name: "LinkedIn",
      icon: Linkedin,
      color: "bg-[#0077b5] hover:bg-[#006399]",
      onClick: () => {
        const url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
          shareUrl
        )}&title=${encodeURIComponent(shareTitle)}`;
        window.open(url, "_blank", "width=600,height=400");
      },
    },
    {
      name: "Twitter",
      icon: Twitter,
      color: "bg-[#1DA1F2] hover:bg-[#1a8cd8]",
      onClick: () => {
        const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
          shareText
        )}&url=${encodeURIComponent(shareUrl)}`;
        window.open(url, "_blank", "width=600,height=400");
      },
    },
    {
      name: "Facebook",
      icon: Facebook,
      color: "bg-[#1877F2] hover:bg-[#166fe5]",
      onClick: () => {
        const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
          shareUrl
        )}`;
        window.open(url, "_blank", "width=600,height=400");
      },
    },
    {
      name: "WhatsApp",
      icon: MessageCircle,
      color: "bg-[#25D366] hover:bg-[#22bf5b]",
      onClick: () => {
        const url = `https://wa.me/?text=${encodeURIComponent(
          `${shareTitle}\n\n${shareText}\n\n${shareUrl}`
        )}`;
        window.open(url, "_blank");
      },
    },
    {
      name: "Email",
      icon: Mail,
      color: "bg-gray-600 hover:bg-gray-700",
      onClick: () => {
        const mailtoUrl = `mailto:?subject=${encodeURIComponent(
          shareTitle
        )}&body=${encodeURIComponent(`${shareText}\n\n${shareUrl}`)}`;
        window.location.href = mailtoUrl;
      },
    },
  ];

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  return (
    <div className="relative">
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        className="flex items-center space-x-2 px-4 py-3 rounded-xl
          bg-white border border-gray-200 text-gray-600
          hover:bg-gray-50 transition-colors"
        onClick={() => setShowShareMenu(true)}
      >
        <Share2 className="w-5 h-5" />
        <span>Share Story</span>
      </motion.button>

      {/* Share Menu Modal */}
      <AnimatePresence>
        {showShareMenu && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
            onClick={() => setShowShareMenu(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 
                w-full max-w-md bg-white rounded-2xl shadow-xl p-6"
            >
              {/* Header */}
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-semibold text-gray-900">
                  Share Story
                </h3>
                <button
                  onClick={() => setShowShareMenu(false)}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              {/* Share Options */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                {shareOptions.map((option) => (
                  <motion.button
                    key={option.name}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={option.onClick}
                    className={`flex items-center space-x-3 px-4 py-3 rounded-xl text-white
                      ${option.color} transition-colors`}
                  >
                    <option.icon className="w-5 h-5" />
                    <span>{option.name}</span>
                  </motion.button>
                ))}
              </div>

              {/* Copy Link */}
              <div className="relative">
                <div className="flex items-center space-x-2 p-3 bg-gray-50 rounded-xl">
                  <Link2 className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  <input
                    type="text"
                    value={shareUrl}
                    readOnly
                    className="w-full bg-transparent text-sm text-gray-600 outline-none"
                  />
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={copyToClipboard}
                    className="flex items-center space-x-1 px-3 py-1.5 rounded-lg
                      bg-white border border-gray-200 text-gray-600 hover:bg-gray-50"
                  >
                    {copied ? (
                      <>
                        <CheckCircle2 className="w-4 h-4 text-green-500" />
                        <span className="text-sm">Copied!</span>
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4" />
                        <span className="text-sm">Copy</span>
                      </>
                    )}
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
const SuccessStoriesView = () => {
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStory, setSelectedStory] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [activeSlide, setActiveSlide] = useState(0);

  const categories = [
    { id: "all", label: "All Stories", icon: Trophy, color: "blue" },
    {
      id: "academic",
      label: "Academic Excellence",
      icon: GraduationCap,
      color: "purple",
    },
    { id: "career", label: "Career Growth", icon: TrendingUp, color: "green" },
    {
      id: "entrepreneurship",
      label: "Entrepreneurship",
      icon: Rocket,
      color: "yellow",
    },
    {
      id: "research",
      label: "Research & Innovation",
      icon: Sparkles,
      color: "pink",
    },
    { id: "social_impact", label: "Social Impact", icon: Heart, color: "red" },
  ];

  useEffect(() => {
    fetchStories();
  }, [selectedCategory]);

  const fetchStories = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/alumni/success-stories${
          selectedCategory !== "all" ? `?category=${selectedCategory}` : ""
        }`
      );
      const data = await response.json();
      setStories(data.stories);
    } catch (error) {
      console.error("Error fetching stories:", error);
    } finally {
      setLoading(false);
    }
  };

  const triggerCelebration = () => {
    // Create clapping effect with emojis
    const emojis = ["👏", "👏", "👏", "👏"];
    const end = Date.now() + 3000;

    const colors = ["#FFD700", "#FFA500", "#FF6347"];

    (function frame() {
      confetti({
        particleCount: 2,
        angle: 60,
        spread: 55,
        origin: { x: 0 },
        colors: colors,
        shapes: ["circle", "square"],
        scalar: 2,
        zIndex: 1000,
      });

      confetti({
        particleCount: 2,
        angle: 120,
        spread: 55,
        origin: { x: 1 },
        colors: colors,
        shapes: ["circle", "square"],
        scalar: 2,
        zIndex: 1000,
      });

      if (Date.now() < end) {
        requestAnimationFrame(frame);
      }
    })();

    // Add emoji animations
    const container = document.createElement("div");
    container.style.position = "fixed";
    container.style.zIndex = "10000";
    container.style.top = "50%";
    container.style.left = "50%";
    container.style.transform = "translate(-50%, -50%)";
    document.body.appendChild(container);

    emojis.forEach((emoji, index) => {
      setTimeout(() => {
        const element = document.createElement("div");
        element.style.position = "absolute";
        element.style.fontSize = "3rem";
        element.style.opacity = "0";
        element.style.animation = "floatUp 1s ease-out forwards";
        element.textContent = emoji;
        container.appendChild(element);

        setTimeout(() => element.remove(), 1000);
      }, index * 200);
    });

    setTimeout(() => container.remove(), 3000);
  };


  const StoryCard = ({ story, index, onView }) => {
    const getMetricColor = (metric, isAfter) => {
      if (isAfter) {
        if (metric.includes('Enhanced') || metric.includes('Developed')) return 'text-emerald-600';
        return 'text-emerald-600';
      }
      return 'text-gray-700';
    };
  
    const getMetricBg = (metric, isAfter) => {
      if (isAfter) {
        if (metric.includes('Enhanced') || metric.includes('Developed')) return 'bg-emerald-100';
        return 'bg-emerald-50';
      }
      return 'bg-gray-50';
    };

    const handleViewStory = () => {
      setSelectedStory(story);
      triggerCelebration();
    };
  
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: index * 0.1 }}
        className="group relative bg-white rounded-2xl shadow-sm hover:shadow-xl 
          overflow-hidden transition-all duration-300"
      >
        {/* Header Image Section */}
        <div className="relative h-52">
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/20 z-10" />
          {story.images?.[0] ? (
            <img
              src={story.images[0]}
              alt={story.title}
              className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-700"
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-violet-500 to-blue-600 flex items-center justify-center">
              <Trophy className="w-16 h-16 text-white/50" />
            </div>
          )}
  
          {/* Featured Badge */}
          {index === 0 && (
            <div className="absolute top-4 right-4 z-20">
              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full 
                bg-gradient-to-r from-amber-500/90 to-orange-500/90 text-white 
                shadow-lg backdrop-blur-sm"
              >
                <Star className="w-4 h-4" />
                <span className="text-sm font-medium">Featured Story</span>
              </div>
            </div>
          )}
  
          {/* Title Overlay */}
          <div className="absolute bottom-0 left-0 right-0 p-4 z-20">
            <h3 className="text-xl font-bold text-white mb-2 line-clamp-2">
              {story.title}
            </h3>
            <div className="flex items-center gap-3 text-white/90 text-sm">
              <div className="flex items-center gap-1.5">
                <Calendar className="w-4 h-4" />
                <time dateTime={story.createdAt}>
                  {new Date(story.createdAt).toLocaleDateString()}
                </time>
              </div>
              {story.college && (
                <div className="flex items-center gap-1.5">
                  <Building className="w-4 h-4" />
                  <span>{story.college.name}</span>
                </div>
              )}
            </div>
          </div>
        </div>
  
        {/* Content Section */}
        <div className="p-6 space-y-6">
          <p className="text-gray-600 line-clamp-2">{story.description}</p>
  
          {/* Metrics Section */}
          {story.beforeAfter && (
            <div className="grid grid-cols-2 gap-4">
              {/* Before */}
              <div className="space-y-3">
                <div className="flex items-center gap-2 mb-2">
                  <div className="p-1.5 rounded-lg bg-red-50">
                    <TrendingDown className="w-4 h-4 text-red-600" />
                  </div>
                  <span className="text-sm font-medium text-gray-800">Before</span>
                </div>
                <div className="space-y-2">
                  {story.beforeAfter.before.metrics?.slice(0, 1).map((metric, idx) => (
                    <div
                      key={idx}
                      className={`p-2 rounded-lg bg-red-50 ${getMetricBg(metric.value, false)} 
                        transition-colors group-hover:shadow-sm`}
                    >
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">{metric.key}</span>
                       
                      </div>
                      <span className={`text-sm font-medium text-red-600 ${getMetricColor(metric.value, false)}`}>
                          {metric.value}
                        </span>
                    </div>
                  ))}
                </div>
              </div>
  
              {/* After */}
              <div className="space-y-3">
                <div className="flex items-center gap-2 mb-2">
                  <div className="p-1.5 rounded-lg bg-emerald-100">
                    <TrendingUp className="w-4 h-4 text-emerald-600" />
                  </div>
                  <span className="text-sm font-medium text-gray-800">After</span>
                </div>
                <div className="space-y-2">
                  {story.beforeAfter.after.metrics?.slice(0, 1).map((metric, idx) => (
                    <div
                      key={idx}
                      className={`p-2 rounded-lg ${getMetricBg(metric.value, true)} 
                        transition-colors group-hover:shadow-sm`}
                    >
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">{metric.key}</span>
                        
                      </div>
                      <span className={`text-sm font-medium text-green-600 ${getMetricColor(metric.value, true)}`}>
                          {metric.value}
                        </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
  
          {/* Action Button */}
          <button
          onClick={handleViewStory}
          className="w-full flex items-center justify-center gap-2 px-4 py-3
            bg-gradient-to-r from-blue-500 to-violet-500 text-white rounded-xl
            hover:from-blue-600 hover:to-violet-600 transition-all
            transform hover:-translate-y-0.5 hover:shadow-lg group/button"
        >
          <Sparkles className="w-4 h-4" />
          <span className="font-medium">View Full Story</span>
          <ArrowRight className="w-4 h-4 transform group-hover/button:translate-x-1 transition-transform" />
        </button>
        </div>
      </motion.div>
    );
  };
  

  const MetricsPreview = ({ title, metrics, variant }) => {
    if (!metrics?.length) return null;

    const bgColor = variant === "before" ? "bg-gray-50" : "bg-green-50";
    const textColor = variant === "before" ? "text-gray-600" : "text-green-600";
    const iconColor = variant === "before" ? "text-gray-400" : "text-green-500";

    return (
      <div className={`p-3 ${bgColor} rounded-lg`}>
        <div className="flex items-center space-x-2 mb-2">
          {variant === "before" ? (
            <TrendingDown className={`w-4 h-4 ${iconColor}`} />
          ) : (
            <TrendingUp className={`w-4 h-4 ${iconColor}`} />
          )}
          <span className="text-sm font-medium text-gray-700">{title}</span>
        </div>
        {metrics.slice(0, 2).map((metric, index) => (
          <div key={index} className="flex justify-between items-center">
            <span className="text-xs text-gray-500">{metric.key}</span>
            <span className={`text-sm font-medium ${textColor}`}>
              {metric.value}
            </span>
          </div>
        ))}
      </div>
    );
  };

  // Story Detail Modal
  const StoryDetailModal = ({ story, onClose }) => {
    const [selectedImageIndex, setSelectedImageIndex] = useState(0);
    const allImages = [
      ...(story.images || []),
      ...(story.beforeAfter?.before?.images || []),
      ...(story.beforeAfter?.after?.images || []),
    ].filter(Boolean);

    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[1000] overflow-y-auto"
      >
        {/* Backdrop */}
        <div className="fixed inset-0 bg-black/75 backdrop-blur-sm" onClick={onClose} />

        {/* Modal Content */}
        <div className="relative min-h-screen flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="relative w-full max-w-5xl bg-white rounded-2xl shadow-2xl"
          >
            {/* Header Section */}
            <div className="relative">
              {allImages.length > 0 ? (
                <div className="relative h-96">
                  <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-transparent to-black/50" />
                  <motion.img
                    key={selectedImageIndex}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    src={allImages[selectedImageIndex]}
                    alt={story.title}
                    className="w-full h-full object-cover"
                  />
                  
                  {/* Image Navigation */}
                  {allImages.length > 1 && (
                    <>
                      <button
                        onClick={() => setSelectedImageIndex((prev) => 
                          prev === 0 ? allImages.length - 1 : prev - 1
                        )}
                        className="absolute left-4 top-1/2 -translate-y-1/2 p-2.5 rounded-full
                          bg-black/20 hover:bg-black/40 text-white transition-colors"
                      >
                        <ChevronLeft className="w-6 h-6" />
                      </button>
                      <button
                        onClick={() => setSelectedImageIndex((prev) => 
                          (prev + 1) % allImages.length
                        )}
                        className="absolute right-4 top-1/2 -translate-y-1/2 p-2.5 rounded-full
                          bg-black/20 hover:bg-black/40 text-white transition-colors"
                      >
                        <ChevronRight className="w-6 h-6" />
                      </button>
                    </>
                  )}
                </div>
              ) : (
                <div className="h-96 bg-gradient-to-br from-blue-600 to-violet-600 
                  flex items-center justify-center">
                  <Trophy className="w-24 h-24 text-white/50" />
                </div>
              )}

              {/* Close Button */}
              <button
                onClick={onClose}
                className="absolute top-4 right-4 p-2 rounded-full bg-black/20 
                  hover:bg-black/40 text-white transition-colors"
              >
                <X className="w-6 h-6" />
              </button>

              {/* Action Buttons - Moved to header */}
              <div className="absolute bottom-4 left-4 right-4 flex justify-between items-center">
                <div className="flex items-center space-x-3">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={triggerCelebration}
                    className="flex items-center space-x-2 px-5 py-2.5 rounded-xl
                      bg-gradient-to-r from-amber-500 to-orange-500 text-white
                      shadow-lg shadow-orange-500/25 hover:shadow-orange-500/40
                      transform hover:-translate-y-0.5 transition-all"
                  >
                    <Star className="w-5 h-5" />
                    <span className="font-medium">Celebrate</span>
                  </motion.button>

                  <ShareButton story={story} />
                </div>

                <div className="flex items-center space-x-3">
                  <span className="px-4 py-2 rounded-xl bg-white/90 backdrop-blur-sm 
                    text-gray-900 text-sm font-medium">
                    {new Date(story.createdAt).toLocaleDateString('en-US', {
                      month: 'long',
                      day: 'numeric',
                      year: 'numeric'
                    })}
                  </span>
                  {story.college && (
                    <div className="flex items-center space-x-2 px-4 py-2 rounded-xl 
                      bg-white/90 backdrop-blur-sm text-gray-900">
                      <Building className="w-4 h-4" />
                      <span className="text-sm font-medium">{story.college.name}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Content Section */}
            <div className="p-8">
              {/* Category and Title */}
              <div className="mb-8">
                <span className="px-4 py-1.5 rounded-full text-sm font-semibold
                  bg-blue-50 text-blue-600 border border-blue-100">
                  {categories.find(c => c.id === story.category)?.label || story.category}
                </span>
                <h2 className="text-3xl font-bold text-gray-900 mt-4 mb-3">{story.title}</h2>
                <p className="text-lg text-gray-600 leading-relaxed">{story.description}</p>
              </div>

              {/* Impact Overview */}
              {story.beforeAfter && (
                <div className="mb-12">
                  <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                    <LineChart className="w-6 h-6 text-blue-600 mr-2" />
                    Impact Overview
                  </h3>

                  <div className="grid md:grid-cols-2 gap-8">
                    {/* Before Section */}
                    <div className="space-y-6">
                      <div className="flex items-center space-x-3">
                        <div className="p-2 rounded-lg bg-gray-100">
                          <Clock className="w-5 h-5 text-gray-600" />
                        </div>
                        <h4 className="text-lg font-semibold text-gray-900">
                          Initial State
                        </h4>
                      </div>

                      <div className="bg-gray-50 rounded-xl p-6">
                        <p className="text-gray-600 mb-4">
                          {story.beforeAfter.before.description}
                        </p>

                        {story.beforeAfter.before.metrics?.length > 0 && (
                          <div className="space-y-3">
                            {story.beforeAfter.before.metrics.map(
                              (metric, index) => (
                                <div
                                  key={index}
                                  className="flex items-center justify-between p-3
                                bg-white rounded-lg border border-gray-100"
                                >
                                  <span className="font-medium text-gray-700">
                                    {metric.key}
                                  </span>
                                  <span className="text-gray-900">
                                    {metric.value}
                                  </span>
                                </div>
                              )
                            )}
                          </div>
                        )}
                      </div>

                      {/* Before Images */}
                      {story.beforeAfter.before.images?.length > 0 && (
                        <div className="grid grid-cols-2 gap-4">
                          {story.beforeAfter.before.images.map(
                            (image, index) => (
                              <img
                                key={index}
                                src={image}
                                alt={`Before state ${index + 1}`}
                                className="rounded-lg w-full h-48 object-cover"
                              />
                            )
                          )}
                        </div>
                      )}
                    </div>

                    {/* After Section */}
                    <div className="space-y-6">
                      <div className="flex items-center space-x-3">
                        <div className="p-2 rounded-lg bg-green-100">
                          <Target className="w-5 h-5 text-green-600" />
                        </div>
                        <h4 className="text-lg font-semibold text-gray-900">
                          Results Achieved
                        </h4>
                      </div>

                      <div className="bg-green-50 rounded-xl p-6">
                        <p className="text-gray-600 mb-4">
                          {story.beforeAfter.after.description}
                        </p>

                        {story.beforeAfter.after.metrics?.length > 0 && (
                          <div className="space-y-3">
                            {story.beforeAfter.after.metrics.map(
                              (metric, index) => (
                                <div
                                  key={index}
                                  className="flex items-center justify-between p-3
                                bg-white rounded-lg border border-green-100"
                                >
                                  <span className="font-medium text-gray-700">
                                    {metric.key}
                                  </span>
                                  <span className="text-green-600 font-semibold">
                                    {metric.value}
                                  </span>
                                </div>
                              )
                            )}
                          </div>
                        )}
                      </div>

                      {/* After Images */}
                      {story.beforeAfter.after.images?.length > 0 && (
                        <div className="grid grid-cols-2 gap-4">
                          {story.beforeAfter.after.images.map(
                            (image, index) => (
                              <img
                                key={index}
                                src={image}
                                alt={`After state ${index + 1}`}
                                className="rounded-lg w-full h-48 object-cover"
                              />
                            )
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex flex-wrap items-center justify-between gap-4 pt-6 border-t">
                

                {story.college && (
                  <div className="flex items-center space-x-2 text-gray-500">
                    <Building className="w-5 h-5" />
                    <span>{story.college.name}</span>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      </motion.div>
    );
  };
  const stats = {
    totalStories: stories.length,
    totalImpact: stories.reduce((sum, story) => {
      const afterMetrics = story.beforeAfter?.after?.metrics || [];
      return sum + afterMetrics.length;
    }, 0),
    uniqueCategories: [...new Set(stories.map((story) => story.category))]
      .length,
  };
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      {/* Hero Section */}
      <HeroSection
        title="Success Stories"
        subtitle="Discover inspiring journeys of transformation and achievement from our community. "
        showSearch={true}
        searchConfig={{
          value: searchTerm,
          onChange: (e) => setSearchTerm(e.target.value),
          placeholder: "Search success stories by title, description...",
        }}
        statsConfig={[
          { value: stats.totalStories.toString(), label: "Success Stories" },
          { value: stats.totalImpact.toString(), label: "Impact Metrics" },
          { value: stats.uniqueCategories.toString(), label: "Categories" },
        ]}
      />

    {/* Category Navigation */}
    <div className="max-w-7xl mx-auto px-4 pt-8">
        <div className="flex flex-wrap gap-3">
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => setSelectedCategory(category.id)}
              className={`flex items-center space-x-2 px-4 py-2 rounded-xl text-sm font-medium
                transition-all ${
                selectedCategory === category.id
                  ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/25'
                  : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              <category.icon className="w-4 h-4" />
              <span>{category.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Stories Grid */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4">
          {loading ? (
            <div className="flex items-center justify-center min-h-[400px]">
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full"
              />
            </div>
          ) : (
            <>
              {stories.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {stories
                    .filter(story =>
                      story.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                      story.description.toLowerCase().includes(searchTerm.toLowerCase())
                    )
                    .map((story, index) => (
                      <StoryCard key={story._id} story={story} index={index} />
                    ))}
                </div>
              ) : (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-center py-16"
                >
                  <div className="bg-white rounded-2xl p-8 max-w-lg mx-auto shadow-xl border border-gray-100">
                    <div className="p-4 mb-6 mx-auto w-20 h-20 rounded-full bg-gray-50 
                      flex items-center justify-center">
                      <SearchX className="w-10 h-10 text-gray-400" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">
                      No Stories Found
                    </h3>
                    <p className="text-gray-600 mb-6">
                      We couldn't find any success stories matching your criteria. 
                      Try adjusting your search or check back later for new stories!
                    </p>
                    <button
                      onClick={() => {
                        setSearchTerm("");
                        setSelectedCategory("all");
                      }}
                      className="inline-flex items-center space-x-2 px-6 py-3 
                        bg-gray-900 text-white rounded-xl hover:bg-gray-800 
                        transition-colors"
                    >
                      <span>View All Stories</span>
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                </motion.div>
              )}
            </>
          )}

          {/* Load More Button - Show if there are more stories */}
          {stories.length > 0 && (
            <div className="mt-12 text-center">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="inline-flex items-center space-x-2 px-6 py-3 
                  bg-white border border-gray-200 rounded-xl text-gray-600
                  hover:bg-gray-50 hover:border-gray-300 transition-all"
              >
                <span className="font-medium">Load More Stories</span>
                <ChevronRight className="w-4 h-4" />
              </motion.button>
            </div>
          )}
        </div>
      </section>


      {/* Story Detail Modal */}
      <AnimatePresence>
        {selectedStory && (
          <StoryDetailModal
            story={selectedStory}
            onClose={() => setSelectedStory(null)}
          />
        )}
      </AnimatePresence>

      {/* Custom Styles */}
      <style jsx global>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }

        @keyframes floatUp {
          0% {
            transform: translate(-50%, 0);
            opacity: 0;
          }
          50% {
            opacity: 1;
          }
          100% {
            transform: translate(-50%, -100px);
            opacity: 0;
          }
        }

        /* Add smooth scroll behavior */
        html {
          scroll-behavior: smooth;
        }

        /* Add custom transitions */
        .transition-transform {
          transition-property: transform;
          transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
          transition-duration: 150ms;
        }
      `}</style>
    </div>
  );
};

export default SuccessStoriesView;